/**
 * Axios 请求封装
 * 
 * 来源: 文件34(API-DB-AUTH-BOUNDARIES) + 文件68(FRONTEND-ACTION-API-TRACE-MATRIX)
 * 
 * 功能:
 * - 统一 baseURL: /api/v1
 * - 认证: Web用Cookie+CSRF（开发阶段用Bearer Token）
 * - 错误处理: 统一拦截ApiError，按retryable决定是否重试
 * - 分页: 自动处理PageResult<T>
 * - 幂等: 命令请求自动注入idempotencyKey
 * - 字段转换: camelCase(前端) ↔ snake_case(后端)
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ElMessage } from 'element-plus'
import type { ApiError, PageResult } from './types'

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

const request: AxiosInstance = axios.create({
  baseURL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ===== 请求拦截器 =====
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 注入认证Token
    const token = localStorage.getItem('gs_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 注入幂等键（POST/PATCH/DELETE）
    if (['post', 'patch', 'delete'].includes(config.method || '')) {
      const data = config.data as Record<string, unknown> | undefined
      if (data && !data.idempotencyKey) {
        data.idempotencyKey = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// ===== 响应拦截器 =====
request.interceptors.response.use(
  (response: AxiosResponse) => {
    // 后端统一返回 { code, data, message }，这里直接解包取 data
    const body = response.data
    if (body && typeof body === 'object' && 'code' in body) {
      if (body.code === 0) {
        // 分页响应：保留 items/total/page/size
        if ('total' in body) {
          return { items: body.data || body.items || [], data: body.data, total: body.total, page: body.page, size: body.pageSize || body.size, pageSize: body.pageSize }
        }
        return body.data
      }
      // 业务错误
      ElMessage.error(body.message || '操作失败')
      return Promise.reject(body)
    }
    return body
  },
  (error) => {
    if (error.response) {
      const apiError: ApiError = error.response.data || {}
      const status = error.response.status

      // 401: 会话失效
      if (status === 401) {
        localStorage.removeItem('gs_token')
        localStorage.removeItem('gs_user')
        window.location.href = '/login'
        return Promise.reject(apiError)
      }

      // 403: 权限不足
      if (status === 403) {
        ElMessage.error('权限不足，无法执行此操作')
        return Promise.reject(apiError)
      }

      // 409: 冲突（重复提交/资源冲突）
      if (status === 409) {
        ElMessage.warning(apiError.message || '操作冲突，请刷新后重试')
        return Promise.reject(apiError)
      }

      // 422: 字段校验错误
      if (status === 422 && apiError.fieldErrors) {
        const firstError = Object.values(apiError.fieldErrors)[0]?.[0]
        ElMessage.error(firstError || apiError.message || '数据校验失败')
        return Promise.reject(apiError)
      }

      // 5xx: 服务端错误
      if (status >= 500) {
        ElMessage.error(apiError.retryable ? '服务暂时不可用，请稍后重试' : '服务异常，请联系管理员')
        return Promise.reject(apiError)
      }

      // 其他错误
      ElMessage.error(apiError.message || '请求失败')
    } else if (error.code === 'ECONNABORTED') {
      ElMessage.error('请求超时，请检查网络后重试')
    } else {
      ElMessage.error('网络异常，请检查连接')
    }

    return Promise.reject(error)
  }
)

// ===== 封装方法 =====

export async function get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return request.get(url, config)
}

export async function post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return request.post(url, data, config)
}

export async function patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return request.patch(url, data, config)
}

export async function put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return request.put(url, data, config)
}

export async function del<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return request.delete(url, config)
}

// 分页查询
export async function getPage<T = unknown>(
  url: string,
  params?: { page?: number; size?: number; [key: string]: unknown }
): Promise<PageResult<T>> {
  const res = await request.get(url, { params: { page: 1, size: 20, ...params } })
  // 后端okPaginated返回 { data, total, page, pageSize }，统一映射到 { items, page, size, total }
  if (res && typeof res === 'object' && 'data' in res && !('items' in res)) {
    return {
      items: (res as any).data || [],
      page: (res as any).page || 1,
      size: (res as any).pageSize || (res as any).size || 20,
      total: (res as any).total || 0,
    }
  }
  return res as PageResult<T>
}

export default request
